// Who may reach the TAS policy Override (`ignorePolicies`) on the approval page.
// The flag is a permanent, one-way, application-wide bypass — it makes the
// employee unconditionally eligible and clears the annual/lifetime limit and
// employment-status blocks, and no un-override path exists — so the audience is
// named here rather than inferred from which app happens to be running.

/**
 * TIO-staff roles trusted with the override. Being admitted to tio-admin is not
 * the same as holding this: the admin app also hosts payment ops, account
 * managers, devops, and the audit-only cohort, none of whom judge an
 * application against its policies.
 */
const STAFF_OVERRIDE_RELATIONSHIP_TYPES: readonly string[] = [
  'TIO_ADMIN',
  'TAS.Approver.TIO_APPROVER',
];

/**
 * Company-side roles trusted with the override. Deliberately narrower than the
 * app's general TAS-editing audience: supervisors and the rest of the approver
 * ladder approve applications but must not be able to switch the policies off.
 */
const COMPANY_OVERRIDE_RELATIONSHIP_TYPES: readonly string[] = ['ACCOUNT_OWNER', 'EMPLOYEE_ADMIN'];

/**
 * The subset of a computed role view this gate reads. `isActive` is the
 * *relationship's* flag, which the `user.isAccountOwner` / `isEmployeeAdmin`
 * getters never consult — a severed relationship still answers true there, and a
 * dead admin relationship must not carry a one-way policy bypass.
 */
export interface OverrideRoleView {
  relationshipType?: string;
  isActive?: boolean;
}

function holdsLiveRole(
  roles: readonly OverrideRoleView[] | undefined,
  relationshipTypes: readonly string[]
): boolean {
  return (
    roles?.some(
      (role) =>
        role.isActive === true &&
        !!role.relationshipType &&
        relationshipTypes.includes(role.relationshipType)
    ) ?? false
  );
}

/** True when the user holds a live TIO_ADMIN or TAS.Approver.TIO_APPROVER relationship. */
export function hasStaffOverrideRole(roles: readonly OverrideRoleView[] | undefined): boolean {
  return holdsLiveRole(roles, STAFF_OVERRIDE_RELATIONSHIP_TYPES);
}

/** True when the user holds a live ACCOUNT_OWNER or EMPLOYEE_ADMIN relationship. */
export function hasCompanyOverrideRole(roles: readonly OverrideRoleView[] | undefined): boolean {
  return holdsLiveRole(roles, COMPANY_OVERRIDE_RELATIONSHIP_TYPES);
}

export interface PolicyOverrideAccess {
  /** Running in tio-admin — the TIO-staff surface. */
  isAdminApp: boolean;
  /** Running in tio-employee — the company-side surface. */
  isEmployeeApp: boolean;
  /** The acting user's computed role views. */
  roles: readonly OverrideRoleView[] | undefined;
}

/**
 * tio-admin admits far more than the two staff roles that judge applications —
 * payment ops and account managers by relationship, and the permission-scoped
 * audit cohort with no legacy role at all, which reaches this footer through the
 * audit queue's embedded approval pane. The employee app admits company admins
 * and account owners only. Any other host app (the agent workspace, and anything
 * added later) gets no override at all: opting in should be a decision, not a
 * side effect of mounting the approval page.
 */
export function canAccessPolicyOverride({
  isAdminApp,
  isEmployeeApp,
  roles,
}: PolicyOverrideAccess): boolean {
  if (isAdminApp) {
    return hasStaffOverrideRole(roles);
  }
  if (!isEmployeeApp) {
    return false;
  }
  return hasCompanyOverrideRole(roles);
}
